import type { AppState, SubscriptionTier } from '../types';

export interface CustomerWorkspace {
  userId: string;
  name: string;
  email: string;
  estateIds: string[];
  tier?: SubscriptionTier;
  isProfessional?: boolean;
  status?: 'active' | 'pending';
  isPending?: boolean;
}

// UI scope only: the authenticated administrator and complete account cache stay intact.
export function customerWorkspaceState(state: AppState, estateIds: string[]): AppState {
  const ids = new Set(estateIds);
  const estates = (state.estates || []).filter((item) => ids.has(item.id));
  const plans = (state.plans || []).filter((item) => ids.has(item.estateId));
  const selectedEstateId = estates.some((item) => item.id === state.selectedEstateId)
    ? state.selectedEstateId
    : (estates[0]?.id ?? '');
  const selectedPlanId = plans.some(
    (item) => item.id === state.selectedPlanId && item.estateId === selectedEstateId,
  )
    ? state.selectedPlanId
    : (plans.find((item) => item.estateId === selectedEstateId)?.id ?? '');
  return {
    ...state,
    estates,
    fields: (state.fields || []).filter((item) => ids.has(item.estateId)),
    plans,
    activities: (state.activities || []).filter((item) => ids.has(item.estateId)),
    activityHistory: (state.activityHistory || []).filter((item) => ids.has(item.estateId)),
    attachments: (state.attachments || []).filter((item) => ids.has(item.estateId)),
    selectedEstateId,
    selectedPlanId,
  };
}

export function mergeCustomerWorkspace(
  complete: AppState,
  next: AppState,
  estateIds: string[],
): AppState {
  const ids = new Set(estateIds);
  if ((next.estates || []).some((item) => !ids.has(item.id)))
    throw new Error('Opret og tilknyt nye revirer fra administrationen.');
  const merge = <T extends { id: string; estateId: string }>(all: T[], scoped: T[]): T[] => {
    const foreignIds = new Set(
      all.filter((item) => !ids.has(item.estateId)).map((item) => item.id),
    );
    if (scoped.some((item) => !ids.has(item.estateId) || foreignIds.has(item.id)))
      throw new Error('Ændringen ligger uden for den åbne kundes arbejdsplads.');
    return [...all.filter((item) => !ids.has(item.estateId)), ...scoped];
  };
  if (next.selectedEstateId && !next.estates.some((item) => item.id === next.selectedEstateId))
    throw new Error('Det valgte revir tilhører ikke den åbne kunde.');
  if (
    next.selectedPlanId &&
    !next.plans.some(
      (item) => item.id === next.selectedPlanId && item.estateId === next.selectedEstateId,
    )
  )
    throw new Error('Den valgte plan tilhører ikke den åbne kunde.');
  return {
    ...complete,
    estates: [
      ...(complete.estates || []).filter((item) => !ids.has(item.id)),
      ...(next.estates || []),
    ],
    fields: merge(complete.fields || [], next.fields || []),
    plans: merge(complete.plans || [], next.plans || []),
    activities: merge(complete.activities || [], next.activities || []),
    activityHistory: merge(complete.activityHistory ?? [], next.activityHistory ?? []),
    attachments: merge(complete.attachments || [], next.attachments || []),
    selectedEstateId: next.selectedEstateId,
    selectedPlanId: next.selectedPlanId,
  };
}
